import { useState, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { css } from "@emotion/react"
import { Button, Typography } from "matsuri-ui"
import ArrowRightIcon from "@mui/icons-material/ArrowRight"
import CheckIcon from "@mui/icons-material/Check"
import { M2MKeyVerticalIcon, M2MPasskeyIcon } from "m2m-icons"
import dayjs from "dayjs"
import { theme } from "@/theme"
import { useTranslation } from "@/hooks/useTranslation"
import { KeyRotationPhotoModal } from "@/parts/KeyRotationPhotoModal"
import {
    useCallbackExecution,
    CALLBACK_TYPES
} from "@/hooks/useCallbackExecution"
import {
    SPARE_KEY_NUMBER_UNREGISTERED,
    SPARE_KEY_ROTATION_CONTACT_ADMIN
} from "@/model/spareKeybox"

export interface KeyboxRotationCardProps {
    type: "guest" | "spare"
    cleaningId: string
    cleaningDate: string
    currentKeyboxNumber?: string
    /** 変更前の番号が番号未登録（物件運営情報にも履歴にも無い）かどうか */
    isCurrentKeyboxNumberUnregistered?: boolean
    keyboxNumber: string
    isRotated: boolean
    installationMethod?: string
    location?: string
    outLocation?: string
    hasPhotoUrl?: boolean
    rotate: (photoFile?: File) => Promise<void>
}

export const KeyboxRotationCard = ({
    type,
    cleaningId,
    cleaningDate,
    currentKeyboxNumber,
    isCurrentKeyboxNumberUnregistered = false,
    keyboxNumber,
    isRotated,
    location,
    outLocation,
    hasPhotoUrl,
    rotate
}: KeyboxRotationCardProps) => {
    const navigate = useNavigate()
    const { t } = useTranslation()
    const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false)
    const callbackExecution = useCallbackExecution(cleaningId)

    const locationText =
        location && outLocation
            ? `${location}・${outLocation}`
            : location || outLocation || t("不明")
    const typeText = type === "spare" ? t("スペアキー") : t("ゲストキー")

    // 清掃日が過ぎているかどうかをチェック
    const isCleaningDatePassed = useMemo(() => {
        const today = dayjs().startOf("day")
        const cleaning = dayjs(cleaningDate).startOf("day")
        return today.isAfter(cleaning)
    }, [cleaningDate])

    // 別清掃で変更済みかどうか（isRotated=trueかつ清掃日が過ぎている）
    const isChangedByOtherCleaning = isRotated && isCleaningDatePassed

    const handleRotationClick = async () => {
        setIsPhotoModalOpen(true)
    }

    const handlePhotoConfirm = async (photoFile: File) => {
        await rotate(photoFile)
        setIsPhotoModalOpen(false)
    }

    const handleCannotChange = () => {
        if (cleaningId) {
            const callbackType =
                type === "spare"
                    ? CALLBACK_TYPES.SPARE_KEY_COMPLETION
                    : CALLBACK_TYPES.GUEST_KEY_COMPLETION
            const { callbackId, callbackType: createdCallbackType } =
                callbackExecution.createCallback(cleaningId, callbackType)

            // トラブル報告画面に遷移
            navigate(`/cleanings/${cleaningId}/trouble`, {
                state: { callbackId, callbackType: createdCallbackType }
            })
        }
    }

    return (
        <div
            css={css`
                display: flex;
                flex-direction: column;
                align-items: center;
                width: 100%;
                padding: 16px 20px;
            `}
        >
            <header
                css={css`
                    display: flex;
                    gap: 8px;
                    align-items: center;
                `}
            >
                {type === "spare" ? (
                    <M2MKeyVerticalIcon size={24} />
                ) : (
                    <M2MPasskeyIcon size={24} />
                )}
                <Typography bolder>{typeText}</Typography>
            </header>
            {isChangedByOtherCleaning ? (
                <div
                    css={css`
                        padding: 16px;
                        margin-top: 16px;
                        text-align: center;
                        background-color: ${theme.palette.background.default};
                        border-radius: 8px;
                    `}
                >
                    <Typography>
                        {t(
                            "別清掃にて変更済みです。どうしても知る必要がある場合は管理者へお知らせください"
                        )}
                    </Typography>
                </div>
            ) : (
                <div
                    css={css`
                        display: grid;
                        grid-template-columns: repeat(3, 1fr);
                        margin-top: 16px;
                        & > div {
                            display: grid;
                            place-items: center;
                        }
                    `}
                >
                    <div>
                        <Typography variant="caption">{t("変更前")}</Typography>
                        <Typography>
                            {isCurrentKeyboxNumberUnregistered
                                ? t(SPARE_KEY_NUMBER_UNREGISTERED)
                                : currentKeyboxNumber || t("不明")}
                        </Typography>
                    </div>
                    <div>
                        <ArrowRightIcon
                            css={css`
                                margin-right: 16px;
                                margin-left: 16px;
                                font-size: 48px;
                            `}
                        />
                    </div>
                    <div>
                        <Typography variant="caption">{t("変更後")}</Typography>
                        <Typography bolder>{keyboxNumber}</Typography>
                    </div>
                </div>
            )}
            {isCurrentKeyboxNumberUnregistered && !isRotated && (
                <Typography
                    variant="caption"
                    css={css`
                        margin-top: 8px;
                        color: ${theme.palette.text.secondary};
                    `}
                >
                    {t(SPARE_KEY_ROTATION_CONTACT_ADMIN)}
                </Typography>
            )}
            <div
                css={css`
                    display: flex;
                    gap: 8px;
                    margin-top: 16px;
                `}
            >
                <Typography
                    css={css`
                        min-width: 100px;
                    `}
                >
                    {t("設置場所：")}
                </Typography>
                <Typography>{locationText}</Typography>
            </div>
            <footer
                css={css`
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    width: 100%;
                    margin-top: 32px;
                `}
            >
                {/*
                    変更前の番号が番号未登録（isCurrentKeyboxNumberUnregistered）でも、変更ボタンは意図的に押せるようにしている。
                    変更前の番号はシステムの外（現地での管理者への問い合わせ、たまたま知っていた など）で分かることがあり、
                    分かった時点でそのままローテできるようにするため。変更後の番号は番号未登録でも m2m-checkin が必ず発行する。
                    番号未登録の物件で「変更前の番号をシステムから知る手段は用意しない」という決定事項は、ローテの操作を禁止する意味ではない
                */}
                {isRotated ? (
                    <div
                        css={css`
                            display: flex;
                            flex: 1;
                            gap: 8px;
                            justify-content: center;
                        `}
                    >
                        <CheckIcon
                            css={css`
                                color: ${theme.palette.intentions.primary.main};
                            `}
                        />
                        <Typography>
                            {hasPhotoUrl
                                ? t("変更済み")
                                : t("トラブル報告済み")}
                        </Typography>
                    </div>
                ) : (
                    <div
                        css={css`
                            display: flex;
                            flex-direction: column;
                            gap: 12px;
                        `}
                    >
                        <Button
                            variant="filled"
                            color="primary"
                            fullWidth
                            onClick={handleRotationClick}
                        >
                            {type === "spare"
                                ? t("スペアキーを変更しました")
                                : t("ゲストキーを変更しました")}
                        </Button>
                        <Button
                            variant="outlined"
                            color="primary"
                            fullWidth
                            onClick={handleCannotChange}
                        >
                            {t("鍵を変更できません")}
                        </Button>
                    </div>
                )}
            </footer>

            <KeyRotationPhotoModal
                isOpen={isPhotoModalOpen}
                onClose={() => setIsPhotoModalOpen(false)}
                onConfirm={handlePhotoConfirm}
            />
        </div>
    )
}
